import {
    ArcRadiusConstraint,
    CircleDiameterConstraint,
    CircleConcentricConstraint,
    CircleCoradialConstraint,
    CircleCurveLengthEqualConstraint,
    CircleEqualConstraint,
    CircleFixConstraint,
} from './relationDefinition/circle';
import {
    LineAngleConstraint,
    LineCoincidentConstraint,
    LineCollinearConstraint,
    LineEqualConstraint,
    LineFixConstraint,
    LineHorizontalConstraint,
    LineIntersectionConstraint,
    LineParallelConstraint,
    LinePerpendicularConstraint,
    LineVerticalConstraint,
    SketchLineRelationConstraint,
} from './relationDefinition/line';
import {
    PointCoincidentConstraint,
    PointDistanceConstraint,
    PointFixConstraint,
    PointMidpointConstraint,
} from './relationDefinition/point';
import {SketchRelationKind} from './relation-compatibility/SketchRelationKinds';
import {MidpointConstraint} from "./relationDefinition/line/MidPointConstraint";
import {TangentConstraint} from "./relationDefinition";
import {ThreePointAngleConstraint} from './relationDefinition/point/ThreePointAngleConstraint';
import {SlotFixConstraint} from './relationDefinition/slot/SlotFixConstraint';
import {SlotEqualConstraint} from './relationDefinition/slot/SlotEqualConstraint';
import {SketchPatternedConstraint} from './relationDefinition/pattern/SketchPatternedConstraint';


export type {GeometricConstraintClass} from './GeometricConstraintClass';
export {SketchLineRelationConstraint} from './relationDefinition/line';
export * from './relationDefinition/line';
export * from './relationDefinition/point';
export * from './relationDefinition/circle';
export * from './relationDefinition/SketchEntitySymmetricConstraint';

/**
 * 关系类型到约束类的映射类型。
 */
type RelationConstraintClassMap = Partial<Record<SketchRelationKind, any>>;

/*
 * 映射惰性构建说明（重要）：
 * 1. constraint 包与 mesh/sketch 包之间存在历史循环依赖，模块求值顺序不稳定；
 * 2. 若在模块顶层用对象字面量急切构建映射，处于循环中未完成初始化的约束类绑定会以
 *    undefined 被固化进映射（键在、值为 undefined），且随调用方 import 顺序不同而时好时坏；
 * 3. 因此四张映射一律改为“首次调用时构建 + 记忆化”，调用发生在运行期，
 *    此时所有模块已完成初始化，映射值恒为真实约束类。
 */

/** 线关系约束类映射缓存。 */
let lineRelationConstraintClassMapCache: RelationConstraintClassMap | null = null;

/**
 * 解析线关系约束类映射（惰性构建）。
 * @returns 线关系约束类映射。
 */
export function resolveLineRelationConstraintClassMap(): RelationConstraintClassMap {
    if (!lineRelationConstraintClassMapCache) {
        lineRelationConstraintClassMapCache = {
            [SketchRelationKind.Horizontal]: LineHorizontalConstraint,
            [SketchRelationKind.Vertical]: LineVerticalConstraint,
            [SketchRelationKind.Fix]: LineFixConstraint,
            [SketchRelationKind.Parallel]: LineParallelConstraint,
            [SketchRelationKind.Perpendicular]: LinePerpendicularConstraint,
            [SketchRelationKind.Collinear]: LineCollinearConstraint,
            [SketchRelationKind.Equal]: LineEqualConstraint,
            [SketchRelationKind.Coincident]: LineCoincidentConstraint,
            [SketchRelationKind.Midpoint]: MidpointConstraint,
            [SketchRelationKind.Tangent]: TangentConstraint,
            /**
             * 倒角落地时创建的两类关系（`SketchChamferOperationService` → `bindLineIntersectionRelation`
             * / `bindLineAngleRelation`）。本映射同时是持久化"支持关系集合"的事实来源
             * （`resolveSupportedPersistentRelationKindSet` 取四张表键的并集），
             * 缺登记会让含倒角的草图在存盘时抛"暂不支持关系类型"。
             */
            [SketchRelationKind.Intersection]: LineIntersectionConstraint,
            [SketchRelationKind.Angle]: LineAngleConstraint,
            // Patterned 只由阵列领域服务使用带参构造，本映射仅声明持久化支持边界。
            [SketchRelationKind.Patterned]: SketchPatternedConstraint,
        };
    }
    return lineRelationConstraintClassMapCache;
}

/** 点关系约束类映射缓存。 */
let pointRelationConstraintClassMapCache: RelationConstraintClassMap | null = null;

/**
 * 解析点关系约束类映射（惰性构建）。
 * @returns 点关系约束类映射。
 */
export function resolvePointRelationConstraintClassMap(): RelationConstraintClassMap {
    if (!pointRelationConstraintClassMapCache) {
        pointRelationConstraintClassMapCache = {
            [SketchRelationKind.Fix]: PointFixConstraint,
            [SketchRelationKind.Coincident]: PointCoincidentConstraint,
            [SketchRelationKind.Distance]: PointDistanceConstraint,
            [SketchRelationKind.Midpoint]: PointMidpointConstraint,
            // 三点定角（SW ANGLE3P）：圆弧槽口角度尺寸的关系视图即此类型。
            [SketchRelationKind.ThreePointAngle]: ThreePointAngleConstraint,
        };
    }
    return pointRelationConstraintClassMapCache;
}

/** 圆关系约束类映射缓存。 */
let circleRelationConstraintClassMapCache: RelationConstraintClassMap | null = null;

/**
 * 解析圆关系约束类映射（惰性构建）。
 * @returns 圆关系约束类映射。
 */
export function resolveCircleRelationConstraintClassMap(): RelationConstraintClassMap {
    if (!circleRelationConstraintClassMapCache) {
        circleRelationConstraintClassMapCache = {
            [SketchRelationKind.Fix]: CircleFixConstraint,
            [SketchRelationKind.Coradial]: CircleCoradialConstraint,
            [SketchRelationKind.Tangent]: TangentConstraint,
            [SketchRelationKind.Concentric]: CircleConcentricConstraint,
            [SketchRelationKind.Equal]: CircleEqualConstraint,
            [SketchRelationKind.EqualCurveLength]: CircleCurveLengthEqualConstraint,
            // 圆的主尺度关系：对齐 SOLIDWORKS，圆标注尺寸后属性栏显示`直径`而非`距离`。
            [SketchRelationKind.Diameter]: CircleDiameterConstraint,
        };
    }
    return circleRelationConstraintClassMapCache;
}

/** 槽口关系约束类映射缓存。 */
let slotRelationConstraintClassMapCache: RelationConstraintClassMap | null = null;

/**
 * 解析槽口关系约束类映射（惰性构建）。
 *
 * 说明：`固定槽口` / `相等槽口` 是 SOLIDWORKS 为槽口单列的两个关系类型
 * （`swConstraintType_FIXEDSLOT` / `SAMESLOTS`）。此前枚举、显示名、规则、图标都已登记，
 * 唯独缺本映射——于是面板列得出按钮、点下去却创建不出约束，也不进持久化支持集。
 *
 * @returns 槽口关系约束类映射。
 */
export function resolveSlotRelationConstraintClassMap(): RelationConstraintClassMap {
    if (!slotRelationConstraintClassMapCache) {
        slotRelationConstraintClassMapCache = {
            [SketchRelationKind.FixSlot]: SlotFixConstraint,
            [SketchRelationKind.EqualSlot]: SlotEqualConstraint,
        };
    }
    return slotRelationConstraintClassMapCache;
}

/** 圆弧自身关系约束类映射缓存。 */
let arcRelationConstraintClassMapCache: RelationConstraintClassMap | null = null;

/**
 * 解析圆弧自身关系约束类映射（惰性构建）。
 * 说明：
 * 1. 仅用于圆弧 self 关系创建；
 * 2. 避免将圆弧专属 Radius 约束误复用到圆/椭圆；
 * 3. Fix 仍复用圆曲线固定约束实现。
 * @returns 圆弧关系约束类映射。
 */
export function resolveArcRelationConstraintClassMap(): RelationConstraintClassMap {
    if (!arcRelationConstraintClassMapCache) {
        arcRelationConstraintClassMapCache = {
            [SketchRelationKind.Fix]: CircleFixConstraint,
            [SketchRelationKind.Radius]: ArcRadiusConstraint,
        };
    }
    return arcRelationConstraintClassMapCache;
}

